import { createCollectionHooks } from '@/hooks/lists/createListHooks'
import { BUYLIST_KEYS } from '@/hooks/lists/useBuylist'
import { api } from '@/services/api'
import type { CollectionItemUpdate } from '@/services/api/collection'
import {
  COLLECTION_KEYS,
  COLLECTION_MUTATION_KEYS,
  SEALED_COLLECTION_KEYS,
  SEALED_MUTATION_KEYS,
} from './collectionKeys'

// Card collection CRUD hooks
const crudHooks = createCollectionHooks<'cardId', CollectionItemUpdate>({
  keys: {
    quantities: COLLECTION_KEYS.quantities,
  },
  mutationKeys: {
    add: COLLECTION_MUTATION_KEYS.add,
    remove: COLLECTION_MUTATION_KEYS.removeByCard,
    update: COLLECTION_MUTATION_KEYS.updateByCard,
    updateByItem: COLLECTION_MUTATION_KEYS.update,
  },
  api: {
    addItem: api.addToCollection,
    removeItem: api.removeFromCollectionByCardId,
    updateByItem: api.updateCollectionByCard,
    updateItem: api.updateCollectionItem,
  },
  group: 'collection',
  // userQuantities reseeds both the collection and buylist quantity maps via
  // setQueryData, so it covers the optimistic state we update in onMutate
  // without needing to invalidate those stub-backed keys directly (which
  // would briefly wipe them and cause a one-frame pill flicker).
  // `collectionItemByCard` drives the card detail "In Collection" badge.
  // `['buylist']` is the GET list view that needs to reflect the drained row.
  // `collectionQuantitiesByBase` drives the cross-variant owned-total badges and deck
  // "Missing N cards" counts; it has no optimistic onMutate stub, so the online
  // path must invalidate it here (the offline-replay defaults already do — see
  // COLLECTION_WRITE_SETTLED_KEYS) or those aggregates go stale for ~5 min (#4).
  settledInvalidation: [
    ['userQuantities'],
    ['dashboardStats'],
    ['collectionItemByCard'],
    ['buylist'],
    ['collectionQuantitiesByBase'],
  ],
  // Removes affect dashboard stats and the cross-variant aggregates; other
  // caches refresh on staleTime.
  removeSettledInvalidation: [['dashboardStats'], ['collectionItemByCard'], ['collectionQuantitiesByBase']],
  // Adding cards drains the matching buylist row (#101).
  drainOnAdd: { quantitiesKey: BUYLIST_KEYS.quantities },
  idKey: 'cardId',
})

export const useCollectionQuantities = crudHooks.useQuantities
export const useAddToCollection = crudHooks.useAdd
export const useRemoveFromCollectionByCard = crudHooks.useRemove
export const useUpdateCollectionByCard = crudHooks.useUpdate as NonNullable<typeof crudHooks.useUpdate>

// Sealed collection CRUD hooks
const sealedCrudHooks = createCollectionHooks<
  'sealedProductId',
  { quantity?: number; notes?: string | null; acquiredPrice?: number | null }
>({
  keys: {
    quantities: SEALED_COLLECTION_KEYS.quantities,
  },
  mutationKeys: {
    add: SEALED_MUTATION_KEYS.add,
    remove: SEALED_MUTATION_KEYS.remove,
    update: SEALED_MUTATION_KEYS.update,
  },
  api: {
    addItem: api.addToSealedCollection,
    removeItem: api.removeFromSealedCollection,
    updateByItem: api.updateSealedCollectionItem,
  },
  group: 'sealedCollection',
  // Only invalidate dashboardStats — quantity map is optimistically updated
  settledInvalidation: [['dashboardStats']],
  removeSettledInvalidation: [['dashboardStats']],
  idKey: 'sealedProductId',
})

export const useSealedCollectionQuantities = sealedCrudHooks.useQuantities
export const useAddToSealedCollection = sealedCrudHooks.useAdd
export const useRemoveFromSealedCollection = sealedCrudHooks.useRemove
